import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Package2, Loader2, Calculator, Ruler, Weight, CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import PesoInput, { pesoParaGramas } from '../components/PesoInput';
import { useToast } from '../hooks/use-toast';
import { calcularFrete, criarEnvio, walletSaldo, buscarCep, listarContatos } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { savePending, readPending, clearPending } from '../lib/pendingEnvio';

const formatCEP = (v) => v.replace(/\D/g, '').slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');

const msgErro = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return 'Confira as medidas: comprimento mín. 11 cm, largura mín. 6 cm, altura mín. 0,4 cm (máx. 105 cm) e peso até 30 kg.';
  return 'Tente novamente em instantes.';
};

const CalculatorPage = () => {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const retomou = useRef(false);
  const [authNotice, setAuthNotice] = useState(false);
  const [contatos, setContatos] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState('calc'); // 'calc' | 'emit'
  const [form, setForm] = useState({
    origin: '', destination: '', pesoG: '300',
    length: '16', width: '11', height: '2',
  });
  useEffect(() => {
    const cep = user?.remetente?.endereco?.cep;
    if (cep) setForm((f) => ({ ...f, origin: formatCEP(cep) }));
  }, [user]);
  useEffect(() => {
    if (user) listarContatos().then(setContatos).catch(() => {});
  }, [user]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);

  // Emit form
  const [emitForm, setEmitForm] = useState({
    recipient_name: '', recipient_doc: '', recipient_phone: '',
    logradouro_destino: '', numero_destino: '', complemento_destino: '', bairro_destino: '',
    cidade_destino: '', uf_destino: '', conteudo: 'Mercadoria', valor_conteudo: '',
  });
  const [emitLoading, setEmitLoading] = useState(false);
  const [cienteRegras, setCienteRegras] = useState(false);

  const escolherContato = (id) => {
    const c = contatos.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, destination: formatCEP(c.endereco.cep) }));
    setEmitForm((cur) => ({
      ...cur,
      recipient_name: c.nome, recipient_doc: c.cpf_cnpj || '', recipient_phone: c.telefone || '',
      logradouro_destino: c.endereco.logradouro, numero_destino: c.endereco.numero,
      complemento_destino: c.endereco.complemento || '', bairro_destino: c.endereco.bairro,
      cidade_destino: c.endereco.cidade, uf_destino: c.endereco.uf,
    }));
    setResults(null);
  };

  const calcular = async (f) => {
    const data = await calcularFrete({
      cep_origem: f.origin.replace(/\D/g, ''),
      cep_destino: f.destination.replace(/\D/g, ''),
      peso_g: pesoParaGramas(f.pesoG),
      comprimento: parseFloat(String(f.length).replace(',', '.')) || 16,
      largura: parseFloat(String(f.width).replace(',', '.')) || 11,
      altura: parseFloat(String(f.height).replace(',', '.')) || 2,
    });
    return (data.resultados || []).filter((r) => !r.erro && r.valor > 0).sort((a, b) => a.valor - b.valor);
  };

  const handleCalc = async (e) => {
    e.preventDefault();
    if (form.origin.replace(/\D/g, '').length !== 8 || form.destination.replace(/\D/g, '').length !== 8) {
      toast({ title: 'CEP inválido', description: 'Insira CEPs com 8 dígitos.' });
      return;
    }
    if (pesoParaGramas(form.pesoG) < 1) {
      toast({ title: 'Informe o peso', description: 'Digite o peso do pacote em gramas (até 30.000 g).' });
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const validos = await calcular(form);
      if (validos.length === 0) {
        toast({ title: 'Sem opções disponíveis', description: 'Verifique os CEPs ou dimensões informados.' });
      }
      setResults(validos);
    } catch (err) {
      toast({ title: 'Erro ao calcular', description: msgErro(err) });
    } finally {
      setLoading(false);
    }
  };

  const entrarNaEmissao = (r, f) => {
    if (!user.remetente) {
      savePending({ form: f, servico: r.codigo, emit: emitForm });
      toast({ title: 'Cadastre seu remetente', description: 'Preencha seus dados em Configurações. Depois você volta para este envio.' });
      navigate('/configuracoes');
      return;
    }
    setSelected(r);
    setStep('emit');
    buscarCep(f.destination).then((a) => {
      if (a) setEmitForm((cur) => ({ ...cur, logradouro_destino: cur.logradouro_destino || a.logradouro, bairro_destino: cur.bairro_destino || a.bairro, cidade_destino: a.cidade, uf_destino: a.uf }));
    });
  };

  const handleEscolher = (r) => {
    if (!user) {
      savePending({ form, servico: r.codigo });
      setAuthNotice(true);
      return;
    }
    entrarNaEmissao(r, form);
  };

  const irParaCarteira = () => {
    savePending({ form, servico: selected.codigo, emit: emitForm });
    navigate('/carteira');
  };

  // Retoma a simulação guardada (vinda da página inicial, do login/cadastro, da carteira ou dos dados do remetente).
  useEffect(() => {
    if (authLoading || retomou.current) return;
    const pend = readPending();
    if (!pend || !pend.form) return;
    retomou.current = true;
    const antigo = pend.form.weightLabel ? (pend.form.weightLabel === 'Até 300g' ? '300' : String(parseInt(pend.form.weightLabel, 10) * 1000)) : null;
    const f = { ...pend.form, pesoG: pend.form.pesoG || antigo || '300', origin: user?.remetente ? formatCEP(user.remetente.endereco.cep) : (pend.form.origin || '') };
    setForm(f);
    if (f.origin.replace(/\D/g, '').length !== 8 || f.destination.replace(/\D/g, '').length !== 8) { clearPending(); return; }
    (async () => {
      setLoading(true);
      try {
        const validos = await calcular(f);
        setResults(validos);
        if (pend.servico && user) {
          const r = validos.find((x) => x.codigo === pend.servico);
          if (pend.emit) setEmitForm((cur) => ({ ...cur, ...pend.emit }));
          if (r) entrarNaEmissao(r, f);
        }
        if (!pend.servico || user) clearPending();
      } catch (err) {
        toast({ title: 'Erro ao calcular', description: msgErro(err) });
        clearPending();
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const handleEmit = async (e) => {
    e.preventDefault();
    const valorConteudo = parseFloat(String(emitForm.valor_conteudo).replace(',', '.'));
    if (!emitForm.recipient_name || !emitForm.logradouro_destino || !emitForm.numero_destino || !emitForm.bairro_destino || !emitForm.cidade_destino || !emitForm.uf_destino) {
      toast({ title: 'Preencha os dados do destinatário', description: 'Nome, rua, número, bairro, cidade e UF são obrigatórios.' });
      return;
    }
    if (!cienteRegras) {
      toast({ title: 'Confirme o conteúdo', description: 'Marque que o conteúdo do pacote não é proibido pelos Correios.' });
      return;
    }
    if (!(valorConteudo > 0)) {
      toast({ title: 'Informe o valor do conteúdo', description: 'Usado na declaração de conteúdo dos Correios.' });
      return;
    }
    setEmitLoading(true);
    try {
      const payload = {
        recipient_name: emitForm.recipient_name,
        recipient_doc: emitForm.recipient_doc || null,
        recipient_phone: emitForm.recipient_phone || null,
        cep_origem: form.origin.replace(/\D/g, ''),
        cep_destino: form.destination.replace(/\D/g, ''),
        logradouro_destino: emitForm.logradouro_destino,
        numero_destino: emitForm.numero_destino,
        complemento_destino: emitForm.complemento_destino || null,
        bairro_destino: emitForm.bairro_destino,
        cidade_destino: emitForm.cidade_destino,
        uf_destino: emitForm.uf_destino.toUpperCase().slice(0, 2),
        conteudo: emitForm.conteudo || 'Mercadoria',
        valor_conteudo: valorConteudo,
        ciente_regras: cienteRegras,
        peso_g: pesoParaGramas(form.pesoG),
        comprimento: parseFloat(String(form.length).replace(',', '.')) || 16,
        largura: parseFloat(String(form.width).replace(',', '.')) || 11,
        altura: parseFloat(String(form.height).replace(',', '.')) || 2,
        servico: selected.codigo,
        servico_nome: selected.nome,
      };
      const envio = await criarEnvio(payload);
      await refreshUser();
      toast({ title: 'Envio criado!', description: envio?.bonus > 0 ? 'Etiqueta disponível em Meus envios. Você ganhou R$ 5,00 de bônus no primeiro envio!' : 'Pré-postagem registrada nos Correios. Baixe a etiqueta em Meus envios.' });
      navigate('/envios');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Tente novamente.';
      if (err?.response?.status === 402) {
        toast({ title: 'Saldo insuficiente', description: detail });
        setTimeout(() => navigate('/carteira'), 1500);
      } else {
        toast({ title: 'Erro ao emitir envio', description: typeof detail === 'string' ? detail : 'Confira os campos do formulário.' });
      }
    } finally {
      setEmitLoading(false);
    }
  };

  // ---------------- RENDER ----------------
  const calculatorForm = (
    <div className="bg-white rounded-3xl p-7 border border-slate-100 inn-shadow-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#EAF1FF] flex items-center justify-center">
          <Calculator className="w-6 h-6 text-[#2A5BC7]" />
        </div>
        <div>
          <h2 className="font-bold text-xl text-slate-900">Dados do envio</h2>
          <p className="text-sm text-slate-500">Preencha origem, destino e dimensões</p>
        </div>
      </div>
      <form onSubmit={handleCalc} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-700 tracking-wide">CEP origem</Label>
            <div className="relative mt-1.5">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2A5BC7]" />
              <Input value={form.origin} onChange={(e)=>setForm({...form, origin: formatCEP(e.target.value)})} placeholder="00000-000" readOnly={!!user?.remetente} className={`pl-9 h-11 ${user?.remetente ? 'bg-slate-50 text-slate-600' : ''}`} />
            </div>
            {user?.remetente && <p className="text-[11px] text-slate-500 mt-1">Origem: o endereço do seu remetente. Para mudar, edite em <a href="/configuracoes" className="text-[#2A5BC7] font-semibold">Configurações</a>.</p>}
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-700 tracking-wide">CEP destino</Label>
            <div className="relative mt-1.5">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F77820]" />
              <Input value={form.destination} onChange={(e)=>setForm({...form, destination: formatCEP(e.target.value)})} placeholder="00000-000" className="pl-9 h-11" />
            </div>
            {user && contatos.length > 0 && (
              <select
                defaultValue="" onChange={(e) => { escolherContato(e.target.value); e.target.value = ''; }}
                className="mt-2 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-600"
                aria-label="Escolher cliente salvo"
              >
                <option value="">Escolher cliente salvo...</option>
                {contatos.map((c) => <option key={c.id} value={c.id}>{c.nome} - {c.endereco.cidade}/{c.endereco.uf}</option>)}
              </select>
            )}
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold text-slate-700 tracking-wide flex items-center gap-1"><Weight className="w-3 h-3" /> Peso</Label>
          <PesoInput value={form.pesoG} onChange={(v)=>setForm({...form, pesoG: v})} />
        </div>
        <div>
          <Label className="text-xs uppercase font-semibold text-slate-700 tracking-wide flex items-center gap-1"><Ruler className="w-3 h-3" /> Dimensões (cm)</Label>
          <div className="grid grid-cols-3 gap-3 mt-1.5">
            {[
              { k: 'length', label: 'Comprimento', min: 11 },
              { k: 'width', label: 'Largura', min: 6 },
              { k: 'height', label: 'Altura', min: 0.4 },
            ].map(({ k, label, min }) => (
              <div key={k}>
                <label htmlFor={`dim-${k}`} className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                <Input id={`dim-${k}`} inputMode="decimal" value={form[k]} onChange={(e)=>setForm({...form, [k]: e.target.value})} className="h-11" />
                <span className="block text-[10px] text-slate-400 mt-1">mín. {String(min).replace('.', ',')} cm</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Medidas da embalagem pronta. Se o volume for grande, os Correios cobram pelo peso cubado.</p>
        </div>
        <Button type="submit" disabled={loading} className="w-full inn-btn-primary h-12 rounded-full mt-2">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Consultando Correios...</> : 'Calcular frete com desconto'}
        </Button>
      </form>
    </div>
  );

  const resultsCard = (
    <div className="bg-white rounded-3xl p-7 border border-slate-100 inn-shadow-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#FFF1E5] flex items-center justify-center">
          <Package2 className="w-6 h-6 text-[#F77820]" />
        </div>
        <div>
          <h2 className="font-bold text-xl text-slate-900">Resultado da simulação</h2>
          <p className="text-sm text-slate-500">Escolha a melhor opção para você</p>
        </div>
      </div>

      {!results && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-center text-slate-400">
          <Package2 className="w-12 h-12 mb-3 opacity-50" />
          <p className="text-sm">Preencha o formulário ao lado para ver os preços disponíveis.</p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin mb-3 text-[#2A5BC7]" />
          <p className="text-sm">Consultando os Correios em tempo real...</p>
        </div>
      )}

      {results && results.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mb-2" />
          <p className="font-semibold text-slate-700">Nenhum serviço disponível</p>
          <p className="text-sm text-slate-500 mt-1">Verifique os CEPs informados.</p>
        </div>
      )}

      {results && results.length > 0 && (
        <div className="space-y-3">
          {results.map((c, i) => (
            <div key={c.codigo} className={`rounded-xl p-4 border ${i===0 ? 'border-[#F77820] bg-[#FFF1E5]/40' : 'border-slate-100 bg-slate-50'} hover:bg-[#EAF1FF] transition-colors flex items-center justify-between gap-3`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900">{c.nome}</p>
                  {i === 0 && <span className="text-[10px] font-bold bg-[#F77820] text-white px-2 py-0.5 rounded-full">MELHOR PREÇO</span>}
                </div>
                <p className="text-xs text-slate-500">{c.prazo_dias ? `Entrega em até ${c.prazo_dias} dia(s) útil(eis)` : 'Prazo a confirmar'}</p>
              </div>
              <div className="text-right">
                {c.valor_sem_desconto > c.valor && (
                  <p className="text-xs text-slate-400">
                    Balcão: <span className="line-through">R$ {Number(c.valor_sem_desconto).toFixed(2).replace('.', ',')}</span>
                  </p>
                )}
                <p className="font-extrabold text-xl text-[#2A5BC7]">R$ {Number(c.valor).toFixed(2).replace('.', ',')}</p>
                <Button onClick={() => handleEscolher(c)} className="inn-btn-primary h-8 px-3 rounded-full text-xs mt-1">Escolher</Button>
              </div>
            </div>
          ))}
          <div className="bg-[#EAF1FF] rounded-xl p-4 mt-4 flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#2A5BC7] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700">Valores consultados em tempo real nos Correios. O valor será debitado da sua carteira.</p>
          </div>
        </div>
      )}
    </div>
  );

  const saldoAtual = Number(user?.saldo || 0);
  const semSaldo = !!(selected && user && !user.pre_postagem_direta && saldoAtual < Number(selected.valor));

  const authModal = authNotice && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setAuthNotice(false)}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Entre para emitir">
        <h3 className="font-bold text-xl text-slate-900">Entre para emitir seu frete</h3>
        <p className="text-sm text-slate-600 mt-2">Sua simulação está salva. Entre na sua conta ou crie uma gratuitamente e você continua de onde parou. Ganhe <strong>R$ 5 de bônus</strong> no seu primeiro envio.</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <Button onClick={() => navigate('/login?next=/calcular')} className="inn-btn-primary flex-1 rounded-full h-11">Já tenho conta</Button>
          <Button onClick={() => navigate('/cadastro?next=/calcular')} className="inn-btn-outline flex-1 rounded-full h-11 bg-white">Criar conta grátis</Button>
        </div>
        <button onClick={() => setAuthNotice(false)} className="mt-3 w-full text-xs text-slate-500 hover:text-slate-700">Voltar à simulação</button>
      </div>
    </div>
  );

  const emitView = selected && (
    <div className="bg-white rounded-3xl p-7 border border-slate-100 inn-shadow-card max-w-2xl mx-auto">
      <button onClick={()=>setStep('calc')} className="flex items-center text-sm text-slate-500 hover:text-[#2A5BC7] mb-5">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar para resultados
      </button>
      <div className="flex items-center justify-between bg-gradient-to-r from-[#EAF1FF] to-[#FFF1E5] rounded-xl p-4 mb-6">
        <div>
          <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Serviço escolhido</p>
          <p className="font-bold text-slate-900">{selected.nome}</p>
          <p className="text-xs text-slate-500">{selected.prazo_dias} dia(s) útil(eis)</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">Valor</p>
          <p className="font-extrabold text-2xl text-[#2A5BC7]">R$ {Number(selected.valor).toFixed(2).replace('.', ',')}</p>
        </div>
      </div>

      <h3 className="font-bold text-lg text-slate-900 mb-4">Dados do destinatário</h3>
      <form onSubmit={handleEmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Nome completo *</Label>
            <Input value={emitForm.recipient_name} onChange={(e)=>setEmitForm({...emitForm, recipient_name: e.target.value})} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label>CPF/CNPJ</Label>
            <Input value={emitForm.recipient_doc} onChange={(e)=>setEmitForm({...emitForm, recipient_doc: e.target.value})} className="mt-1.5 h-11" placeholder="000.000.000-00" />
          </div>
        </div>
        <div>
          <Label>Telefone</Label>
          <Input value={emitForm.recipient_phone} onChange={(e)=>setEmitForm({...emitForm, recipient_phone: e.target.value})} className="mt-1.5 h-11" placeholder="(00) 00000-0000" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label>Rua *</Label>
            <Input value={emitForm.logradouro_destino} onChange={(e)=>setEmitForm({...emitForm, logradouro_destino: e.target.value})} className="mt-1.5 h-11" maxLength={50} />
          </div>
          <div>
            <Label>Número *</Label>
            <Input value={emitForm.numero_destino} onChange={(e)=>setEmitForm({...emitForm, numero_destino: e.target.value})} className="mt-1.5 h-11" maxLength={10} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Complemento</Label>
            <Input value={emitForm.complemento_destino} onChange={(e)=>setEmitForm({...emitForm, complemento_destino: e.target.value})} className="mt-1.5 h-11" maxLength={30} />
          </div>
          <div>
            <Label>Bairro *</Label>
            <Input value={emitForm.bairro_destino} onChange={(e)=>setEmitForm({...emitForm, bairro_destino: e.target.value})} className="mt-1.5 h-11" maxLength={30} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label>Cidade *</Label>
            <Input value={emitForm.cidade_destino} onChange={(e)=>setEmitForm({...emitForm, cidade_destino: e.target.value})} className="mt-1.5 h-11" maxLength={30} />
          </div>
          <div>
            <Label>UF *</Label>
            <Input value={emitForm.uf_destino} onChange={(e)=>setEmitForm({...emitForm, uf_destino: e.target.value})} className="mt-1.5 h-11 uppercase" maxLength={2} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Conteúdo *</Label>
            <Input value={emitForm.conteudo} onChange={(e)=>setEmitForm({...emitForm, conteudo: e.target.value})} className="mt-1.5 h-11" maxLength={60} />
          </div>
          <div>
            <Label>Valor do conteúdo (R$) *</Label>
            <Input value={emitForm.valor_conteudo} onChange={(e)=>setEmitForm({...emitForm, valor_conteudo: e.target.value})} className="mt-1.5 h-11" inputMode="decimal" placeholder="0,00" />
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-xl p-3 cursor-pointer">
          <input type="checkbox" checked={cienteRegras} onChange={(e) => setCienteRegras(e.target.checked)} className="mt-1" />
          <span>Declaro que o conteúdo do pacote <strong>não é proibido</strong> pelos Correios e que li <a href="/regras-de-envio" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#2A5BC7] underline">o que pode e o que não pode enviar</a>.</span>
        </label>

        {semSaldo ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
            <div className="flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p><strong>Saldo insuficiente.</strong> Você tem R$ {saldoAtual.toFixed(2).replace('.', ',')} e este envio custa R$ {Number(selected.valor).toFixed(2).replace('.', ',')}. Compre saldo para continuar.</p>
            </div>
            <Button type="button" onClick={irParaCarteira} className="mt-3 inn-btn-primary rounded-full h-10 px-5">Comprar saldo</Button>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {user?.pre_postagem_direta
              ? <p><strong>Atenção:</strong> este envio não será debitado da sua carteira.</p>
              : <p><strong>Atenção:</strong> ao confirmar, R$ {Number(selected.valor).toFixed(2).replace('.', ',')} serão debitados da sua carteira. Saldo atual: <strong>R$ {saldoAtual.toFixed(2).replace('.', ',')}</strong>.</p>}
          </div>
        )}

        <Button type="submit" disabled={emitLoading || semSaldo || !cienteRegras} className="w-full inn-btn-primary h-12 rounded-full">
          {emitLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Emitindo...</> : `Confirmar e debitar R$ ${Number(selected.valor).toFixed(2).replace('.', ',')}`}
        </Button>
      </form>
    </div>
  );

  // Página com layout logado vs deslogado
  if (user) {
    return (
      <AppShell title="Novo envio" subtitle="Calcule e emita seu frete">
        {step === 'calc' ? (
          <div className="grid lg:grid-cols-2 gap-6">{calculatorForm}{resultsCard}</div>
        ) : emitView}
        {authModal}
      </AppShell>
    );
  }

  // Layout público (não logado) — mantém Navbar + Footer
  return (
    <div>
      <Navbar />
      <main className="bg-gradient-to-b from-[#EAF1FF]/40 to-white py-12 lg:py-16 min-h-[60vh]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <span className="inn-stat-pill">Calculadora In&apos;Nova</span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">
              Simule seu <span className="inn-text-gradient">frete</span> em segundos
            </h1>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Compare valores entre serviços dos Correios com desconto.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">{calculatorForm}{resultsCard}</div>
        </div>
      </main>
      <Footer />
      {authModal}
    </div>
  );
};

export default CalculatorPage;
